/**
 * ===================================================================
 * FRONTEND SERVICE - CROP PLANS
 * ===================================================================
 * API sluzba pro spravu planu pestovani.
 */

import { apiClient } from './api';
import {
  CropPlan,
  CropPlanItem,
  CropPlanPlacement,
  CropPlanWithDetails,
  CreateCropPlanDTO,
  UpdateCropPlanDTO,
  CreateCropPlanItemDTO,
  CreatePlacementDTO,
  CropPlanTemplate,
  CropPlanTemplateWithItems,
  CropPlanProgress,
} from '../types/crop-plans';

export const cropPlanService = {
  // ===== PLANS =====

  // Ziskej vsechny plany
  getAllPlans: async (status?: string): Promise<CropPlan[]> => {
    const url = status ? `/crop-plans?status=${status}` : '/crop-plans';
    return apiClient.get<CropPlan[]>(url);
  },

  // Ziskej jeden plan
  getPlan: async (planId: string): Promise<CropPlan> => {
    return apiClient.get<CropPlan>(`/crop-plans/${planId}`);
  },

  // Ziskej plan s detaily (vcetne items a placements)
  getPlanWithDetails: async (planId: string): Promise<CropPlanWithDetails> => {
    return apiClient.get<CropPlanWithDetails>(`/crop-plans/${planId}/details`);
  },

  // Ziskej progress planu
  getPlanProgress: async (planId: string): Promise<CropPlanProgress> => {
    return apiClient.get<CropPlanProgress>(`/crop-plans/${planId}/progress`);
  },

  // Vytvor novy plan
  createPlan: async (data: CreateCropPlanDTO): Promise<CropPlan> => {
    return apiClient.post<CropPlan>('/crop-plans', data as unknown as Record<string, unknown>);
  },

  // Aktualizuj plan
  updatePlan: async (planId: string, data: UpdateCropPlanDTO): Promise<CropPlan> => {
    return apiClient.patch<CropPlan>(
      `/crop-plans/${planId}`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Smaz plan
  deletePlan: async (planId: string): Promise<void> => {
    return apiClient.delete(`/crop-plans/${planId}`);
  },

  // ===== WORKFLOW =====

  // Aktivuj plan
  activatePlan: async (planId: string): Promise<CropPlan> => {
    return apiClient.post<CropPlan>(`/crop-plans/${planId}/activate`, {});
  },

  // Dokonci plan
  completePlan: async (planId: string): Promise<CropPlan> => {
    return apiClient.post<CropPlan>(`/crop-plans/${planId}/complete`, {});
  },

  // Archivuj plan
  archivePlan: async (planId: string): Promise<CropPlan> => {
    return apiClient.post<CropPlan>(`/crop-plans/${planId}/archive`, {});
  },

  // ===== ITEMS =====

  // Ziskej polozky planu
  getItems: async (planId: string): Promise<CropPlanItem[]> => {
    return apiClient.get<CropPlanItem[]>(`/crop-plans/${planId}/items`);
  },

  // Pridej polozku do planu
  addItem: async (planId: string, data: CreateCropPlanItemDTO): Promise<CropPlanItem> => {
    return apiClient.post<CropPlanItem>(
      `/crop-plans/${planId}/items`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Aktualizuj existujici polozku v planu
  updateItem: async (itemId: string, data: { quantity: number; notes?: string; planned_sow_date?: string; planned_transplant_date?: string; planned_harvest_date?: string }): Promise<CropPlanItem> => {
    return apiClient.put<CropPlanItem>(
      `/crop-plans/items/${itemId}`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Odstran polozku z planu
  removeItem: async (itemId: string): Promise<void> => {
    return apiClient.delete(`/crop-plans/items/${itemId}`);
  },

  // ===== PLACEMENTS =====

  // Ziskej umisteni planu
  getPlacements: async (planId: string): Promise<CropPlanPlacement[]> => {
    return apiClient.get<CropPlanPlacement[]>(`/crop-plans/${planId}/placements`);
  },

  // Pridej umisteni k polozce
  addPlacement: async (itemId: string, data: CreatePlacementDTO): Promise<CropPlanPlacement> => {
    return apiClient.post<CropPlanPlacement>(
      `/crop-plans/items/${itemId}/placements`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Aktualizuj stav umisteni
  updatePlacementStatus: async (
    placementId: string,
    status: 'planned' | 'executed' | 'cancelled',
  ): Promise<CropPlanPlacement> => {
    return apiClient.patch<CropPlanPlacement>(`/crop-plans/placements/${placementId}/status`, {
      status,
    });
  },

  // ===== EXECUTION =====

  // Realizuj plan (vytvor batch)
  executePlan: async (planId: string, force?: boolean): Promise<any> => {
    return apiClient.post<any>(`/crop-plans/${planId}/execute`, { force });
  },

  // ===== TEMPLATES =====

  getTemplates: async (): Promise<CropPlanTemplate[]> => {
    return apiClient.get<CropPlanTemplate[]>('/crop-plans/templates');
  },

  getTemplate: async (templateId: string): Promise<CropPlanTemplateWithItems> => {
    return apiClient.get<CropPlanTemplateWithItems>(`/crop-plans/templates/${templateId}`);
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    return apiClient.delete(`/crop-plans/templates/${templateId}`);
  },

  saveAsTemplate: async (planId: string, name: string, description?: string): Promise<CropPlanTemplate> => {
    return apiClient.post<CropPlanTemplate>(`/crop-plans/${planId}/save-as-template`, { name, description });
  },

  createFromTemplate: async (templateId: string, name: string, start_date: string): Promise<CropPlan> => {
    return apiClient.post<CropPlan>('/crop-plans/from-template', { templateId, name, start_date });
  },

  createFromPastPlan: async (pastPlanId: string, name: string, start_date: string): Promise<CropPlan> => {
    return apiClient.post<CropPlan>('/crop-plans/from-past-plan', { pastPlanId, name, start_date });
  },
};
